import * as React from 'react';
import { matchRoutes, RouteObject, useLocation } from 'react-router-dom';
import { setCurrentView } from 'src/helpers/ctaTracking';

/**
 * Resolves the matched route *pattern* — '/job-search/:jobAlias' rather than
 * '/job-search/senior-python-developer-bengaluru-a1b2c3' — and hands it to the
 * CTA tracker on every navigation.
 *
 * This matters more than it looks: with the raw pathname, `page` carries one
 * distinct value per job alias, skill and competition, so every grouping in
 * Amplitude fans out into tens of thousands of one-row buckets and no
 * placement comparison is readable.
 *
 * `matchRoutes` is used rather than `useMatches` because the app mounts a plain
 * <BrowserRouter> with useRoutes; `useMatches` only works under a data router.
 */
const useRoutePattern = (routes: RouteObject[]): void => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    const matches = matchRoutes(routes, pathname);
    if (!matches?.length) {
      setCurrentView(pathname, pathname);
      return;
    }
    // Child paths are relative to their parent ('/jobs' + ':jobAlias/dashboard'),
    // so the pattern is the ancestor chain joined back together. Splats and
    // pathless layout routes contribute nothing.
    const pattern = matches
      .map((match) => match.route.path)
      .filter((path): path is string => !!path && path !== '*')
      .join('/')
      .replace(/\/{2,}/g, '/');

    setCurrentView(pattern.startsWith('/') ? pattern : `/${pattern}`, pathname);
  }, [routes, pathname]);
};

export default useRoutePattern;
